/**
 * Projected proposal value over the contract term — the proposal-side analogue
 * of Cinch's contract-value chart. A stepped area line of monthly billed MRR
 * (so the line visibly steps up when a time-limited discount rolls off), with
 * dashed markers at each promo's end. Recharts, styled with the DS chart tokens.
 */

import { useMemo } from "react";

import {
  Area,
  CartesianGrid,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type { BundleRow, CatalogIndex, DiscountRow, LineRow } from "../calc";
import { PROPOSAL_CHART_COLORS as C } from "../chartColors";
import { formatPrice2 } from "../format";
import { buildProjection, type RenewalConfig } from "../projection";

const COLOR_BRAND = C.brand;
const COLOR_GRID = C.gridLine;
const COLOR_MARKER = C.marker;
const COLOR_TERM = C.term;

interface Props {
  idx: CatalogIndex;
  lines: LineRow[];
  discountRows: DiscountRow[];
  renewal: RenewalConfig;
  bundles?: BundleRow[];
}

export function ProposalTimelineChart({ idx, lines, discountRows, renewal, bundles = [] }: Props) {
  const projection = useMemo(
    () => buildProjection(idx, lines, discountRows, renewal, bundles),
    [idx, lines, discountRows, renewal, bundles],
  );

  const { points, markers, baseMonthly, total, firstTermTotal, secondTermTotal, term, avgMonthly } =
    projection;
  const hasSecondTerm = secondTermTotal > 0;
  // Numeric X (idx = column position) so every marker can render BETWEEN periods (at
  // idx + 0.5) rather than sitting on a period — matches the contract-value chart.
  const realData = points.map((p, i) => ({ ...p, idx: i }));
  const idxByMonth = new Map(points.map((p, i) => [p.month, i]));
  const byIdx = new Map(realData.map((d) => [d.idx, d]));
  // Every marker (term/renewal boundary AND discount roll-off) sits between M{m} and
  // M{m+1}. Drop a synthetic point at that midpoint carrying the *next* month's value so
  // the stepped line changes level exactly under the marker — a non-renewing term falls to
  // $0 at the line, a discount roll-off steps up at the line — instead of half a period
  // later on M{m+1}. Deduped by idx (co-incident markers) and excluded from ticks/domain.
  const boundaryData = markers
    .map((m) => {
      const bi = idxByMonth.get(m.month);
      const next = bi == null ? undefined : points.find((p) => p.month === m.month + 1);
      return bi == null || !next ? null : { idx: bi + 0.5, billed: next.billed, isBoundary: true };
    })
    .filter((d): d is { idx: number; billed: number; isBoundary: true } => d !== null)
    .filter((d, i, arr) => arr.findIndex((o) => o.idx === d.idx) === i);
  const data = [...realData, ...boundaryData].sort((a, b) => a.idx - b.idx);
  const tickFor = (idx: number) => {
    const p = byIdx.get(idx);
    return p ? p.calendarLabel || p.label : "";
  };

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
        <CardTitle className="text-sm">Projected Value ({points.length} mo)</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="neutral">Base {formatPrice2(baseMonthly)}/mo</Badge>
          <Badge variant="neutral">Avg {formatPrice2(avgMonthly)}/mo</Badge>
          <Badge variant="neutral">Term 1 ({term}mo) {formatPrice2(firstTermTotal)}</Badge>
          {hasSecondTerm && (
            <Badge variant="neutral">Term 2 {formatPrice2(secondTermTotal)}</Badge>
          )}
          <Badge variant="info">Total {formatPrice2(total)}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-xs text-muted-foreground">
          Assumes the deal is kept with no changes. The line steps as time-limited discounts roll
          off (amber) and at term / renewal boundaries (blue); usage-based amounts are estimated
          from current quantities.
          {projection.hasStub
            ? " P1 is the day-prorated first period (billing-effective date → month-end); full months bill on the following 1st."
            : ""}
        </p>
        <div className="h-72">
          <ResponsiveContainer>
            <ComposedChart data={data} margin={{ top: 10, right: 16, bottom: 4, left: 8 }}>
              <CartesianGrid stroke={COLOR_GRID} strokeDasharray="2 3" />
              <XAxis
                type="number"
                dataKey="idx"
                domain={[-0.5, realData.length - 0.5]}
                ticks={realData.map((d) => d.idx)}
                tick={{ fontSize: 10 }}
                interval="preserveStartEnd"
                minTickGap={16}
                tickFormatter={tickFor}
              />
              <YAxis
                tick={{ fontSize: 10 }}
                width={68}
                tickFormatter={(v: number) => `$${Number(v).toLocaleString()}`}
              />
              <Tooltip
                formatter={(v) => [formatPrice2(Number(v)), "Billed / mo"]}
                labelFormatter={(idx: number) => {
                  const p = byIdx.get(idx);
                  if (!p) return "";
                  return p.calendarLabel ? `${p.label} · ${p.calendarLabel}` : `Month ${p.month}`;
                }}
              />
              {markers.map((m) => {
                const color = m.kind === "term" ? COLOR_TERM : COLOR_MARKER;
                const baseIdx = idxByMonth.get(m.month);
                if (baseIdx == null) return null;
                // Every marker sits BETWEEN M{m} and M{m+1}, where the stepped line also
                // changes level (via the synthetic boundary point above).
                const x = baseIdx + 0.5;
                return (
                  <ReferenceLine
                    key={`${m.kind}-${m.month}-${m.label}`}
                    x={x}
                    stroke={color}
                    strokeDasharray="5 4"
                    label={{
                      value: m.label,
                      position: m.kind === "term" ? "insideBottomRight" : "insideTopRight",
                      fontSize: 10,
                      fill: color,
                    }}
                  />
                );
              })}
              <Area
                type="stepAfter"
                dataKey="billed"
                stroke={COLOR_BRAND}
                strokeWidth={2}
                fill={COLOR_BRAND}
                fillOpacity={0.12}
                dot={false}
                name="Billed / mo"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
